import { newRelicNoticeError } from '@/utils/newRelicActions';
import BackgroundSVG from './BackgroundSVG';

export type BackgroundTreatmentProps = {
  className?: string;
  children?: React.ReactNode | React.ReactNode[];
  backgroundColor?: string;
  patternColor?: string;
};

/**
 * BackgroundTreatment component renders a background with a squiggly SVG pattern.
 * It ensures that a valid color is provided and handles potential errors gracefully.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.className=''] - Additional class names for the container div.
 * @param {React.ReactNode} props.children - The child elements to be rendered inside the component.
 *
 * @returns {JSX.Element | null} The rendered component or null if an error occurs.
 *
 * @throws Will log a warning and notify New Relic if no color is provided or if an error occurs.
 */
function BackgroundTreatment({
  className = '',
  children,
  backgroundColor = 'bg-primary-700',
  patternColor = 'fill-primary-800',
}: BackgroundTreatmentProps) {
  try {
    return (
      <div className={`not-prose relative w-full overflow-hidden ${className}`}>
        <div
          className={`absolute -z-10 h-full w-full ${backgroundColor} ${patternColor}`}
        >
          <BackgroundSVG />
        </div>
        <div className='z-0'>{children}</div>
      </div>
    );
  } catch (error) {
    console.warn('Error in BackgroundTreatment component:', error);
    newRelicNoticeError(
      new Error(`Error in BackgroundTreatment component: ${error}`),
    );
    return null;
  }
}

export default BackgroundTreatment;
